import { useState } from 'react';
import { useMarkAllRead, useMarkRead, useNotifications } from '../hooks/useNotifications';
import { DataTable, type Column } from '../components/DataTable';
import { type NotificationDto, type QueryOptions } from '../lib/types';

export function Notifications() {
  const [options, setOptions] = useState<QueryOptions>({ page: 1, pageSize: 25 });
  const [unreadOnly, setUnreadOnly] = useState(false);
  const list = useNotifications(options, unreadOnly);
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const columns: Column<NotificationDto>[] = [
    {
      header: 'Title',
      render: (n) => (
        <div className={n.readAt ? 'opacity-60' : 'font-medium'}>
          <div>{n.title}</div>
          <div className="text-xs text-slate-600">{n.body}</div>
        </div>
      ),
    },
    { header: 'Type', render: (n) => <span className="text-xs uppercase">{n.type}</span> },
    { header: 'When', render: (n) => new Date(n.createdAt).toLocaleString() },
    {
      header: '',
      headerClass: 'w-24',
      render: (n) =>
        n.readAt ? (
          <span className="text-xs text-slate-400">Read</span>
        ) : (
          <button
            type="button"
            onClick={() => markRead.mutate(n.id)}
            className="text-xs text-brand-blue hover:underline"
          >
            Mark read
          </button>
        ),
    },
  ];

  // Build matching query string for export links so the export reflects the current
  // search / filter / unread-only state.
  const exportQs = new URLSearchParams();
  if (options.filter) exportQs.set('filter', options.filter);
  if (unreadOnly) exportQs.set('unreadOnly', 'true');
  const exportSuffix = exportQs.toString() ? `?${exportQs}` : '';

  return (
    <section className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-brand-charcoal">Notifications</h1>
          <p className="text-sm text-slate-600">All in-app notifications for your account.</p>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={(e) => {
                setUnreadOnly(e.target.checked);
                setOptions({ ...options, page: 1 });
              }}
            />
            Unread only
          </label>
          <button
            type="button"
            onClick={() => markAllRead.mutate()}
            className="text-brand-blue hover:underline"
          >
            Mark all read
          </button>
        </div>
      </div>

      <DataTable
        data={list.data}
        isLoading={list.isLoading}
        error={list.error}
        options={options}
        onOptionsChange={setOptions}
        columns={columns}
        rowKey={(n) => n.id}
        emptyState={unreadOnly ? 'No unread notifications.' : 'No notifications yet.'}
        searchable
        searchPlaceholder="Search notifications…"
        toolbar={
          <div className="flex items-center gap-2 text-sm">
            <a
              href={`/api/notifications/export.csv${exportSuffix}`}
              className="text-brand-blue hover:underline"
              download
            >
              Export CSV
            </a>
            <span className="text-slate-300">|</span>
            <a
              href={`/api/notifications/export.xlsx${exportSuffix}`}
              className="text-brand-blue hover:underline"
              download
            >
              Export Excel
            </a>
          </div>
        }
      />
    </section>
  );
}
